"use client";
import * as React from "react";
import { GrozdLogo } from "./GrozdLogo";
import { GrapeIllustration } from "./GrapeIllustration";

export interface ErrorPageProps {
  /**
   * Next.js error objekat. Može sadržati `digest` (server-side error ID).
   * Ako nije proslijeđen, generišemo placeholder support code.
   */
  error?: (Error & { digest?: string }) | null;
  /** Reset funkcija iz Next.js error boundary-a. */
  reset?: () => void;
  /** Email adresa podrške za "Pozovi podršku" CTA. Default: support@grozd.me */
  supportEmail?: string;
  /** URL "Idi na početnu" link u headeru. Default "/". */
  homeUrl?: string;
  /** Footer link — Uslovi. */
  termsUrl?: string;
  /** Footer link — Privatnost. */
  privacyUrl?: string;
  /** Footer link — Kontakt. */
  footerContactUrl?: string;
}

/** Generiše support code iz error.digest ili random fallback. */
function generateSupportCode(digest?: string): string {
  if (digest) {
    // Skraćeno na prvih 6 chars + prefix
    return `ERR-Z002-${digest.slice(0, 6)}`;
  }
  // Fallback — pseudo-random 6 chars
  const rand = Math.random().toString(36).slice(2, 8);
  return `ERR-Z002-${rand}`;
}

/**
 * Error stranica — "Nešto nije kako treba."
 *
 * 1:1 port iz mockupa: docs/Dizajn-Disciplina/mockups/404-i-greske.html (Stanje 2).
 *
 * Layout:
 * - Tamni gradient canvas (primary→deep)
 * - Bijela kartica sa ilustracijom palog grozda
 * - Dugmad: "Pokušaj ponovo" (reset()) + "Pozovi podršku" (mailto)
 * - Support code chip (klik = copy to clipboard)
 * - Collapsible "Tehnički detalji" (stack trace, request_id, timestamp)
 *
 * Mora biti client component jer renderujemo error props i koristimo
 * navigator.clipboard + state za toggle detalja.
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
  error,
  reset,
  supportEmail = "support@grozd.me",
  homeUrl = "/",
  termsUrl = "#",
  privacyUrl = "#",
  footerContactUrl = "#",
}) => {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const supportCode = React.useMemo(
    () => generateSupportCode(error?.digest),
    [error?.digest],
  );

  const timestamp = React.useMemo(() => new Date().toISOString(), []);

  const handleCopy = React.useCallback(() => {
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(supportCode).catch(() => {
        /* noop */
      });
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    }
  }, [supportCode]);

  const mailtoHref = `mailto:${supportEmail}?subject=${encodeURIComponent(
    `Grozd — prijava greške ${supportCode}`,
  )}&body=${encodeURIComponent(
    `Kod za podršku: ${supportCode}\nVrijeme: ${timestamp}\n\nOpiši šta si radio kad se desila greška:\n\n`,
  )}`;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, var(--primary) 0%, var(--deep) 100%)",
        padding: 24,
      }}
    >
      {/* Mesh overlay */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 15% 20%, var(--brand-mesh-1, rgba(197,183,238,.22)), transparent 50%), radial-gradient(circle at 85% 85%, var(--brand-mesh-2, rgba(155,134,223,.15)), transparent 55%)",
          pointerEvents: "none",
        }}
      />
      {/* Ghost ilustracija */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          right: -192,
          bottom: -192,
          opacity: 0.04,
          pointerEvents: "none",
        }}
      >
        <svg viewBox="0 0 200 240" width="720" height="720">
          <g
            transform="rotate(30,100,140)"
            fill="none"
            stroke="white"
            strokeWidth="5"
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <path d="M100 68Q114 48 132 40Q124 58 114 66Z" />
            <line x1="100" y1="68" x2="118" y2="52" strokeWidth="3" />
            <circle cx="60" cy="100" r="22" />
            <circle cx="100" cy="100" r="22" />
            <circle cx="140" cy="100" r="22" />
            <circle cx="80" cy="140" r="22" />
            <circle cx="120" cy="140" r="22" />
            <circle cx="100" cy="180" r="22" />
          </g>
        </svg>
      </div>

      {/* Header — PRAVILO 21: constrained na max-width kartice (640px),
          centriran u viewport-u, logo poravnat sa lijevom ivicom kartice. */}
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          margin: "0 auto",
          padding: "32px 24px 16px",
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <a
          href={homeUrl}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            textDecoration: "none",
          }}
        >
          <GrozdLogo size={33} color="#fff" />
          <span
            style={{
              fontFamily: "var(--font-brand, 'Manrope'), system-ui, sans-serif",
              fontWeight: 800,
              fontSize: 20,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            Grozd
          </span>
        </a>
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 16px 40px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{
            margin: "0 auto",
            maxWidth: 640,
            width: "100%",
            background: "var(--surface-card, var(--white))",
            borderRadius: 16,
            border: "1px solid var(--n100)",
            boxShadow: "var(--shadow-card, 0 8px 32px rgba(90,58,181,.18))",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              padding: "40px 40px 48px",
              textAlign: "center",
            }}
          >
            <GrapeIllustration
              size={150}
              tilted
              color="var(--primary)"
              className="grozd-grape-illust"
            />

            <div style={{ marginTop: 48, maxWidth: 420 }}>
              <div
                style={{
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "var(--primary)",
                }}
              >
                Greška
              </div>
              <h1
                style={{
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 800,
                  fontSize: 34,
                  letterSpacing: "-0.02em",
                  color: "var(--ink)",
                  lineHeight: 1.15,
                  marginTop: 8,
                }}
              >
                Nešto nije kako treba.
              </h1>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.6,
                  color: "var(--n600)",
                  maxWidth: 420,
                  marginTop: 8,
                }}
              >
                Desila se greška. Probaj ponovo, a ako se ponovi, javi se
                podršci — biće nam drago da pomognemo.
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 8,
                width: "100%",
                maxWidth: 400,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => reset?.()}
                disabled={!reset}
                style={{
                  flex: 1,
                  minWidth: 160,
                  height: 48,
                  borderRadius: 10,
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: reset ? "pointer" : "not-allowed",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  background: "var(--primary)",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(90,58,181,.25)",
                  opacity: reset ? 1 : 0.6,
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={16}
                  height={16}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="23 4 23 10 17 10" />
                  <path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
                </svg>
                Pokušaj ponovo
              </button>
              <a
                href={mailtoHref}
                style={{
                  flex: 1,
                  minWidth: 160,
                  height: 48,
                  borderRadius: 10,
                  fontFamily:
                    "var(--font-brand, 'Manrope'), system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  background: "var(--surface-card, var(--white))",
                  color: "var(--primary)",
                  border: "1.5px solid var(--primary)",
                  textDecoration: "none",
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={16}
                  height={16}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Pozovi podršku
              </a>
            </div>

            {/* Support code */}
            <div
              style={{
                marginTop: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                fontSize: 13,
                color: "var(--n400)",
                flexWrap: "wrap",
              }}
            >
              <span>Kod za podršku:</span>
              <button
                type="button"
                onClick={handleCopy}
                style={{
                  fontFamily:
                    "var(--font-mono, 'SF Mono', 'Fira Code'), monospace",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--primary)",
                  background: "var(--light)",
                  padding: "4px 10px",
                  borderRadius: 6,
                  letterSpacing: "0.03em",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  border: "1px solid var(--p100)",
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
                aria-label="Kopiraj kod za podršku"
              >
                {copied ? "Kopirano!" : supportCode}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={13}
                  height={13}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ opacity: 0.6 }}
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              </button>
            </div>

            {/* Collapsible tehnički detalji */}
            <div style={{ width: "100%", maxWidth: 480, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => setDetailsOpen((v) => !v)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "var(--n400)",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: "6px 12px",
                  borderRadius: 8,
                  margin: "0 auto",
                  fontFamily: "inherit",
                  transition: "all 150ms cubic-bezier(.22,1,.36,1)",
                }}
                aria-expanded={detailsOpen}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  width={14}
                  height={14}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: detailsOpen ? "rotate(180deg)" : "rotate(0)",
                    transition: "transform 150ms cubic-bezier(.22,1,.36,1)",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                Tehnički detalji
              </button>
              <div
                style={{
                  overflow: "hidden",
                  maxHeight: detailsOpen ? 360 : 0,
                  transition: "max-height 400ms cubic-bezier(.22,1,.36,1)",
                  marginTop: 8,
                }}
              >
                <pre
                  style={{
                    width: "100%",
                    margin: 0,
                    overflow: "auto",
                    borderRadius: 10,
                    border: "1px solid var(--n100)",
                    background: "var(--tint)",
                    padding: "12px 16px",
                    textAlign: "left",
                    fontFamily:
                      "var(--font-mono, 'SF Mono', 'Fira Code'), monospace",
                    fontSize: 12,
                    color: "var(--n600)",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {error?.message ?? "Unknown runtime error"}
                  {error?.stack ? `\n\n${error.stack}` : ""}
                  {`\n\ntimestamp: ${timestamp}`}
                  {error?.digest ? `\ndigest: ${error.digest}` : ""}
                  {`\nsupport_code: ${supportCode}`}
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            margin: "16px auto 0",
            maxWidth: 640,
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 4px",
          }}
        >
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: "var(--p300)",
            }}
          >
            <a
              href={termsUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Uslovi
            </a>
            <span aria-hidden>·</span>
            <a
              href={privacyUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Privatnost
            </a>
            <span aria-hidden>·</span>
            <a
              href={footerContactUrl}
              style={{ color: "var(--p300)", textDecoration: "none" }}
            >
              Kontakt
            </a>
          </nav>
        </div>
      </div>
    </div>
  );
};
